import React from 'react';
import { Modal } from 'ui/base/modal';
import { observer } from 'mobx-react';
import baseStyles from './avatar_input.module.css';
import { StandardPage } from 'ui/base/page/standard_page';
import { BackButton } from 'ui/base/button/back_button';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { AvatarStore } from './avatar_store';
import Messages from './avatar_input_messages';
import { ButtonClickEvent } from 'ui/base/types';
import { AvatarCode as AvatarCodeType } from 'services/accounts/types';
import { AvatarCode } from 'services/accounts/protos/user_dto_pb';
import { avatarImages } from 'ui/standard/avatar/avatar';

const getOrderedAvatars = (): AvatarCodeType[] => {
  const keys = Object.values(AvatarCode);
  keys.sort((a, b) => a - b);
  return keys as AvatarCodeType[];
};

type AvatarInputStyles = {
  modalContainer: string;
  headerContainer: string;
  optionsContainer: string;
  avatarContainer: string;
  baseAvatar: string;
  highlightedAvatar: string;
  ok: string;
};

const themedStyles: ThemedStyles<AvatarInputStyles> = {
  [Theme.DAYLIGHT]: {
    modalContainer: baseStyles.modalContainer,
    headerContainer: baseStyles.headerContainer,
    optionsContainer: baseStyles.optionsContainer,
    avatarContainer: baseStyles.avatarContainer,
    baseAvatar: baseStyles.baseAvatar,
    highlightedAvatar: baseStyles.daylightHighlightedAvatar,
    ok: baseStyles.daylightOk,
  },
  [Theme.MIDNIGHT]: {
    modalContainer: baseStyles.modalContainer,
    headerContainer: baseStyles.headerContainer,
    optionsContainer: baseStyles.optionsContainer,
    avatarContainer: baseStyles.avatarContainer,
    baseAvatar: baseStyles.baseAvatar,
    highlightedAvatar: baseStyles.midnightHighlightedAvatar,
    ok: baseStyles.midnightOk,
  },
};

type AvatarOptionProps = {
  store: AvatarStore;
  avatarCode: AvatarCodeType;
  highlighted: boolean;
};

const AvatarOption = observer(({
  store,
  avatarCode,
  highlighted,
}: AvatarOptionProps) => {
  const setHighlighted = React.useCallback((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.setHighlighted(avatarCode);
  }, [store, avatarCode]);

  const styles = useStyles(themedStyles);
  return (
    <button type='button' className={styles.avatarContainer} onClick={setHighlighted}>
      <img
        src={avatarImages[avatarCode]}
        className={highlighted ? styles.highlightedAvatar : styles.baseAvatar}
        alt='avatar'
      />
    </button>
  );
});

type AvatarInputModalProps = {
  store: AvatarStore;
};

const AvatarInputModal = observer(({ store }: AvatarInputModalProps) => {
  const styles = useStyles(themedStyles);
  const handleOk = React.useCallback((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.commitHighlighted();
    store.modal.hideModal();
  }, [store]);

  return (
    <div className={styles.modalContainer}>
      <StandardPage>
        <div className={styles.headerContainer}>
          <BackButton onClick={store.modal.hideModal}/>
          <button type='button' onClick={handleOk} className={styles.ok}>{Messages.ok()}</button>
        </div>
        <div className={styles.optionsContainer}>
          {getOrderedAvatars().map((avatarCode: AvatarCodeType) => (
            <AvatarOption
              key={avatarCode}
              store={store}
              avatarCode={avatarCode}
              highlighted={avatarCode === store.highlighted}
            />
          ))}
        </div>
      </StandardPage>
    </div>
  );
});

type SelectedAvatarProps = {
  store: AvatarStore;
};

const SelectedAvatar = observer(({ store }: SelectedAvatarProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.reloadHighlighted();
    store.modal.showModal();
  }, [store]);

  return (
    <button type='button' onClick={onShowModal} className={styles.avatarContainer}>
      <img src={avatarImages[store.value]} className={styles.baseAvatar} alt='avatar'/>
    </button>
  );
});

export type AvatarInputProps = {
  store: AvatarStore;
};

export const AvatarInput = ({ store }: AvatarInputProps) => {
  return (
    <>
      <Modal store={store.modal}>
        <AvatarInputModal store={store}/>
      </Modal>
      <SelectedAvatar store={store}/>
    </>
  );
};
